import React from "react"
import Footer from "../components/footer"
import Navbar from "../components/nav"

function AiImageDetectionPolicy() {
  return (
    <>
      <div>
        <header className="header-area">
          <div className="navbar-area">
            <div className="container">
              <div className="row">
                <div className="col-lg-12">
                  <Navbar />
                </div>
              </div>
            </div>
          </div>
        </header>

        <div id="home">
          <div className="container mt-125 mb-50">
            <div className="">
              <div className="card-body shadow">
                <h1>Privacy Policy</h1>
                <h4 className="mt-3">AI Image Detector - Fake Check</h4>
                <p className="mt-2"><strong>Last Updated: December 20, 2024</strong></p>
                
                <hr className="my-4" />
                
                {/* Introduction */}
                <h4 className="mt-4" id="introduction">Introduction</h4>
                <p className="mt-3">
                  Welcome to AI Image Detector ("we," "our," or "us"). We are committed to 
                  protecting your privacy and ensuring you have a positive experience when 
                  using our application. This Privacy Policy explains how we handle information 
                  when you use our AI Image Detector mobile application (the "App").
                </p>
                <p className="mt-3">
                  <strong className="text-primary">
                    Our Core Privacy Principle: Your photos and data never leave your device.
                  </strong>
                </p>

                <hr className="my-4" />

                {/* Information We Do NOT Collect */}
                <h4 className="mt-4" id="not-collected">Information We Do NOT Collect</h4>
                <p className="mt-3">
                  AI Image Detector is designed with privacy as a fundamental principle. 
                  We want to be completely transparent about what we do NOT do:
                </p>
                
                <h5 className="mt-4">Images and Photos</h5>
                <ul className="mt-2" style={{listStyle: 'none', paddingLeft: '10px'}}>
                  <li>❌ We do NOT upload your images to any server</li>
                  <li>❌ We do NOT store your images on external servers</li>
                  <li>❌ We do NOT share your images with third parties</li>
                  <li>❌ We do NOT use your images for AI training</li>
                  <li>❌ We do NOT access your images without your explicit action</li>
                </ul>

                <h5 className="mt-4">Personal Information</h5>
                <ul className="mt-2" style={{listStyle: 'none', paddingLeft: '10px'}}>
                  <li>❌ We do NOT collect your name, email, or contact information</li>
                  <li>❌ We do NOT require account registration</li>
                  <li>❌ We do NOT track your location</li>
                  <li>❌ We do NOT collect device identifiers for tracking purposes</li>
                  <li>❌ We do NOT sell any data to third parties</li>
                </ul>

                <h5 className="mt-4">Usage Data</h5>
                <ul className="mt-2" style={{listStyle: 'none', paddingLeft: '10px'}}>
                  <li>❌ We do NOT use analytics services</li>
                  <li>❌ We do NOT track which images you scan</li>
                  <li>❌ We do NOT monitor your app usage patterns</li>
                  <li>❌ We do NOT use advertising SDKs</li>
                </ul>

                <hr className="my-4" />

                {/* How the App Works */}
                <h4 className="mt-4" id="how-it-works">How the App Works</h4>
                
                <h5 className="mt-4">Offline Processing</h5>
                <p className="mt-2">
                  AI Image Detector operates <strong>100% offline</strong>. All image analysis 
                  is performed locally on your device using on-device machine learning models. 
                  This means:
                </p>
                <ul className="mt-2">
                  <li><strong>No Internet Required:</strong> The app functions completely without an internet connection</li>
                  <li><strong>Local Processing:</strong> All AI detection algorithms run on your device's processor</li>
                  <li><strong>No Data Transmission:</strong> Your images are never transmitted over the internet</li>
                  <li><strong>Instant Privacy:</strong> Analysis results exist only on your device</li>
                </ul>

                <h5 className="mt-4">Image Access</h5>
                <p className="mt-2">When you use the App:</p>
                <ul className="mt-2">
                  <li>You explicitly select images from your gallery or camera</li>
                  <li>Images are processed in your device's memory</li>
                  <li>Analysis results are stored locally on your device</li>
                  <li>You can delete your scan history at any time</li>
                </ul>

                <hr className="my-4" />

                {/* Data Stored on Your Device */}
                <h4 className="mt-4" id="local-data">Data Stored on Your Device</h4>
                <p className="mt-3">
                  The following data is stored <strong>locally on your device only</strong>:
                </p>

                <h5 className="mt-4">Scan History</h5>
                <ul className="mt-2">
                  <li>Thumbnail previews of scanned images</li>
                  <li>Analysis results and confidence scores</li>
                  <li>Timestamps of when scans were performed</li>
                  <li>This data is stored in the app's private storage area</li>
                </ul>

                <h5 className="mt-4">App Preferences</h5>
                <ul className="mt-2">
                  <li>Theme settings (light/dark mode)</li>
                  <li>Language preferences</li>
                  <li>Detection sensitivity settings</li>
                  <li>Notification preferences</li>
                </ul>

                <h5 className="mt-4">How to Delete Your Data</h5>
                <p className="mt-2">You can delete all locally stored data by:</p>
                <ol className="mt-2">
                  <li>Clearing the scan history within the app</li>
                  <li>Clearing the app's data in your device settings</li>
                  <li>Uninstalling the application</li>
                </ol>

                <hr className="my-4" />

                {/* Permissions We Request */}
                <h4 className="mt-4" id="permissions">Permissions We Request</h4>

                <h5 className="mt-4">Camera Permission (Optional)</h5>
                <ul className="mt-2">
                  <li><strong>Purpose:</strong> To take photos directly for analysis</li>
                  <li><strong>Usage:</strong> Only activated when you tap the camera button</li>
                  <li><strong>Storage:</strong> Photos taken are processed and optionally saved to your gallery</li>
                </ul>

                <h5 className="mt-4">Storage/Media Permission</h5>
                <ul className="mt-2">
                  <li><strong>Purpose:</strong> To access images you select for analysis</li>
                  <li><strong>Usage:</strong> Only accesses images you explicitly choose</li>
                  <li><strong>Scope:</strong> Read-only access to selected files</li>
                </ul>

                <h5 className="mt-4">Internet Permission</h5>
                <ul className="mt-2">
                  <li><strong>Purpose:</strong> Not used for data collection</li>
                  <li><strong>Note:</strong> While the app may request this permission, it is not required for core functionality. The app works 100% offline.</li>
                </ul>

                <hr className="my-4" />

                {/* Third-Party Services */}
                <h4 className="mt-4" id="third-party">Third-Party Services</h4>

                <h5 className="mt-4">We Do NOT Use:</h5>
                <ul className="mt-2">
                  <li>Google Analytics or Firebase Analytics</li>
                  <li>Facebook SDK</li>
                  <li>Advertising networks</li>
                  <li>Crash reporting services that collect personal data</li>
                  <li>Any third-party tracking services</li>
                </ul>

                <h5 className="mt-4">Google Play Services</h5>
                <p className="mt-2">
                  The app is distributed through Google Play Store, which has its own privacy policy. 
                  Please refer to <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                  Google's Privacy Policy</a> for information about how Google handles data related to app distribution.
                </p>

                <hr className="my-4" />

                {/* Children's Privacy */}
                <h4 className="mt-4" id="childrens-privacy">Children's Privacy</h4>
                <p className="mt-3">
                  AI Image Detector does not knowingly collect any personal information from 
                  children under the age of 13. Since we do not collect any personal information 
                  from any users, this applies to all age groups equally.
                </p>

                <hr className="my-4" />

                {/* Data Security */}
                <h4 className="mt-4" id="security">Data Security</h4>
                <p className="mt-3">Since all data remains on your device:</p>
                <ul className="mt-2">
                  <li>Your images are protected by your device's security measures</li>
                  <li>We recommend using device encryption and screen lock</li>
                  <li>No data is transmitted, so there is no risk of interception</li>
                  <li>Your scan history is stored in the app's private directory, inaccessible to other apps</li>
                </ul>

                <hr className="my-4" />

                {/* Your Rights */}
                <h4 className="mt-4" id="your-rights">Your Rights</h4>
                <p className="mt-3">You have complete control over your data:</p>
                <ul className="mt-2">
                  <li><strong>Access:</strong> All your data is on your device and accessible to you</li>
                  <li><strong>Deletion:</strong> Delete scan history anytime within the app</li>
                  <li><strong>Portability:</strong> Your data exists only on your device</li>
                  <li><strong>Opt-out:</strong> No tracking to opt out of</li>
                </ul>

                <hr className="my-4" />

                {/* International Users */}
                <h4 className="mt-4" id="international">International Users</h4>
                <p className="mt-3">
                  AI Image Detector can be used anywhere in the world. Since no data is collected or transmitted:
                </p>
                <ul className="mt-2">
                  <li>No cross-border data transfers occur</li>
                  <li>GDPR, CCPA, and other privacy regulations are inherently satisfied</li>
                  <li>Your data stays in your jurisdiction (your device)</li>
                </ul>

                <hr className="my-4" />

                {/* Changes to This Privacy Policy */}
                <h4 className="mt-4" id="changes">Changes to This Privacy Policy</h4>
                <p className="mt-3">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by:
                </p>
                <ul className="mt-2">
                  <li>Updating the "Last Updated" date at the top of this policy</li>
                  <li>Posting the new Privacy Policy on this page</li>
                  <li>Notifying through app updates when significant changes occur</li>
                </ul>
                <p className="mt-3">
                  We encourage you to review this Privacy Policy periodically for any changes.
                </p>

                <hr className="my-4" />

                {/* Open Source Components */}
                <h4 className="mt-4" id="open-source">Open Source Components</h4>
                <p className="mt-3">AI Image Detector uses the following open-source machine learning frameworks:</p>
                <ul className="mt-2">
                  <li>ONNX Runtime for on-device inference</li>
                  <li>These components process data locally and do not transmit information</li>
                </ul>

                <hr className="my-4" />

                {/* Contact Us */}
                <h4 className="mt-4" id="contact">Contact Us</h4>
                <p className="mt-3">
                  If you have any questions about this Privacy Policy or our privacy practices, 
                  please contact us at:
                </p>
                <p className="mt-2"><strong>Email:</strong> mwxapps@gmail.com</p>
                <p><strong>Developer:</strong> MWX Apps</p>

                <hr className="my-4" />

                {/* Summary Table */}
                <h4 className="mt-4" id="summary">Summary</h4>
                <div className="table-responsive mt-3">
                  <table className="table table-bordered">
                    <thead className="thead-light">
                      <tr>
                        <th>Data Type</th>
                        <th>Collected?</th>
                        <th>Shared?</th>
                        <th>Stored Where?</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Images/Photos</td>
                        <td>❌ No</td>
                        <td>❌ No</td>
                        <td>Device only (temporary)</td>
                      </tr>
                      <tr>
                        <td>Personal Info</td>
                        <td>❌ No</td>
                        <td>❌ No</td>
                        <td>N/A</td>
                      </tr>
                      <tr>
                        <td>Location</td>
                        <td>❌ No</td>
                        <td>❌ No</td>
                        <td>N/A</td>
                      </tr>
                      <tr>
                        <td>Usage Analytics</td>
                        <td>❌ No</td>
                        <td>❌ No</td>
                        <td>N/A</td>
                      </tr>
                      <tr>
                        <td>Scan History</td>
                        <td>✅ Local only</td>
                        <td>❌ No</td>
                        <td>Your device</td>
                      </tr>
                      <tr>
                        <td>Preferences</td>
                        <td>✅ Local only</td>
                        <td>❌ No</td>
                        <td>Your device</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <hr className="my-4" />

                <div className="text-center mt-4">
                  <h5 className="text-primary">AI Image Detector - Your Privacy, Our Priority</h5>
                  <p className="mt-2"><em>This privacy policy is effective as of December 20, 2024.</em></p>
                </div>

              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  )
}

export default AiImageDetectionPolicy
